/**
 * App-level session-expired listener (Fix 6, dashboard half).
 *
 * The API client dispatches SESSION_EXPIRED_EVENT on a 401 with no live
 * Clerk session (see lib/api/sessionExpired.ts). This single listener,
 * mounted once in App.tsx, clears the React Query cache (no stale data from
 * the dead session) and bounces to /sign-in?expired=1 carrying the current
 * path+search as redirect_url so the user lands back where they were after
 * re-auth (SignInPage already reads redirect_url through safeRedirect).
 */
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { SESSION_EXPIRED_EVENT } from '../lib/api/sessionExpired';
import { isExtensionSessionRoute } from '../lib/extensionSessionRoute';

export function useSessionExpiredRedirect(): void {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  useEffect(() => {
    const onSessionExpired = () => {
      if (isExtensionSessionRoute(location.pathname)) return;
      // Already on sign-in: a self-redirect would overwrite redirect_url
      // with /sign-in and strand the user after re-auth.
      if (location.pathname.startsWith('/sign-in')) return;
      queryClient.clear();
      const target = `${location.pathname}${location.search}`;
      navigate(`/sign-in?expired=1&redirect_url=${encodeURIComponent(target)}`, { replace: true });
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
  }, [navigate, queryClient, location.pathname, location.search]);
}
